import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import placeholderPfp from "../assets/test_images/mock_profile_picture_1.png";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import FeedHeader from "../components/FeedHeader";
import sha256 from "crypto-js/sha256";
import ReferralLink from "../components/payment/ReferralLink";

const BuyerProfile = () => {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [profile, setProfile] = useState({
    fullName: "",
    email: "",
    phone: "",
    country: "",
  });
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [isBuyer, setIsBuyer] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  const [passwordChangeStatus, setPasswordChangeStatus] = useState("");

  // Track form changes
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    country: "",
  });

  // Sync form state with profile when entering edit mode
  useEffect(() => {
    if (editMode) {
      setForm({
        fullName: profile.fullName,
        email: profile.email,
        phone: profile.phone,
        country: profile.country,
      });
    }
  }, [editMode, profile]);

  const handleEdit = () => {
    setEditMode((e) => !e);
    setSaveError("");
  };
  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };
  const handleLogOut = () => {
    axios
      .post(
        "https://genz-staging.feeltiptop.com/api/user/logout",
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        },
      )
      .then((response) => {
        localStorage.removeItem("authToken");
        localStorage.removeItem("feedState");
        navigate("/login");
      })
      .catch((error) => {
        console.error("Logout error:", error);
      });
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError("");
    setPasswordChangeStatus("");
    const token = localStorage.getItem("authToken");
    // Split fullName into firstName and lastName
    const [firstName, ...rest] = form.fullName.trim().split(" ");
    const lastName = rest.join(" ");
    const payload = {
      firstName: firstName || "",
      lastName: lastName || "",
      email: form.email,
      phone: form.phone,
      country: form.country,
      address: form.address,
      bio: form.bio,
      website: form.website,
    };
    let passwordChanged = false;
    // Handle password change if fields are filled
    if (form.currentPassword && form.newPassword) {
      try {
        // Hash passwords with SHA-256 before sending, to match login flow
        const currentPasswordHash = sha256(form.currentPassword).toString();
        const newPasswordHash = sha256(form.newPassword).toString();
        const resp = await axios.put(
          "https://genz-staging.feeltiptop.com/api/user/change_password",
          {
            currentPassword: currentPasswordHash,
            newPassword: newPasswordHash,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setPasswordChangeStatus("Password updated successfully");
        passwordChanged = true;
        setForm((f) => ({ ...f, currentPassword: "", newPassword: "" }));
      } catch (err) {
        setPasswordChangeStatus(
          err.response?.data?.error || "Failed to update password",
        );
        setSaving(false);
        return;
      }
    }
    // Save profile fields (excluding password fields)
    try {
      const response = await axios.put(
        "https://genz-staging.feeltiptop.com/api/user/buyerprofile",
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setProfile({
        fullName:
          `${response.data.firstName || ""} ${response.data.lastName || ""}`.trim(),
        email: response.data.email || "",
        phone: response.data.phone || "",
        country: response.data.country || "",
        address: response.data.address || "",
        bio: response.data.bio || "",
        website: response.data.website || "",
      });
      setPaymentMethods(response.data.paymentMethods || []);
      setEditMode(false);
    } catch (err) {
      setSaveError("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      navigate("/login");
    } else {
      // Fetch header info for FeedHeader
      axios
        .get("https://genz-staging.feeltiptop.com/api/user/getCurrentUserInfo", {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          setFirstName(response.data.firstName || "");
          setLastName(response.data.lastName || "");
          setUsername(response.data.username || "");
        })
        .catch((error) => {
          // fallback: do nothing, keep blank
        });
      // Fetch buyer profile info
      axios
        .get("https://genz-staging.feeltiptop.com/api/user/buyerprofile", {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          const data = response.data;
          setProfile({
            fullName: `${data.firstName || ""} ${data.lastName || ""}`.trim(),
            email: data.email || "",
            phone: data.phone || "",
            country: data.country || "",
          });
          setPaymentMethods(data.paymentMethods || []);
          setLoading(false);
        })
        .catch((err) => {
          setError("Failed to load profile");
          setLoading(false);
        });
      // Fetch user role for Buyer/Seller tag
      axios
        .get("https://genz-staging.feeltiptop.com/api/user/role", {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((response) => {
          setIsBuyer(response.data.isBuyer === true);
          setIsSeller(response.data.isSeller === true);
        })
        .catch(() => {
          setIsBuyer(false);
          setIsSeller(false);
        });
    }
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-lg text-gray-600">Loading profile...</div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-lg text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <>
      <FeedHeader
        onLogout={handleLogOut}
        firstName={firstName}
        lastName={lastName}
        username={username}
      />
      <div className="flex min-h-screen items-start justify-center bg-gray-50 pt-24">
        <div className="w-full max-w-7xl">
          <button
            className="mb-4 flex items-center text-blue-600 hover:underline"
            onClick={handleBack}
          >
            <ArrowLeftIcon className="mr-1 h-5 w-5" /> Back
          </button>
          <div className="rounded-2xl bg-white shadow-lg">
            {/* Blue header area */}
            <div className="h-30 relative flex w-full items-start justify-end rounded-t-2xl bg-gradient-to-r from-blue-300 to-blue-400 p-6">
              {editMode ? (
                <button
                  className="rounded-xl bg-blue-500 px-6 py-2 text-lg font-semibold text-white shadow-md disabled:opacity-60"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              ) : (
                <button
                  className="rounded-xl bg-blue-500 px-6 py-2 text-lg font-semibold text-white shadow-md"
                  onClick={handleEdit}
                >
                  Edit
                </button>
              )}
            </div>
            {/* Profile section: pfp left, text left-aligned next to it, referral link right */}
            <div className="-mt-2 mb-4 flex w-full flex-row items-start justify-between gap-8 px-16">
              <div className="flex flex-row items-end gap-8">
                <div className="flex h-40 w-40 items-center justify-center rounded-full border-4 border-white bg-white shadow-md">
                  <span className="text-xl font-semibold text-gray-700">
                    Profile
                  </span>
                </div>
                <div className="flex flex-col justify-end">
                  <div className="text-left text-2xl font-semibold text-gray-900">
                    {editMode ? (
                      <input
                        type="text"
                        className="w-full rounded-lg border bg-gray-50 px-2 py-1 text-2xl font-semibold text-gray-900"
                        value={form.fullName}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, fullName: e.target.value }))
                        }
                        disabled={saving}
                      />
                    ) : (
                      profile.fullName
                    )}
                  </div>
                  <div className="text-left text-base text-gray-500">
                    {editMode ? (
                      <input
                        type="email"
                        className="w-full rounded-lg border bg-gray-50 px-2 py-1 text-base text-gray-900"
                        value={form.email}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, email: e.target.value }))
                        }
                        disabled={saving}
                      />
                    ) : (
                      profile.email
                    )}
                  </div>
                  {(isBuyer || isSeller) && (
                    <div className="mt-2 flex gap-2">
                      {isBuyer && (
                        <span className="inline-block rounded-full bg-blue-50 px-4 py-1 text-sm font-medium text-blue-600">
                          Buyer
                        </span>
                      )}
                      {isSeller && (
                        <span className="inline-block rounded-full bg-green-50 px-4 py-1 text-sm font-medium text-green-600">
                          Seller
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 flex h-full flex-col items-start justify-start">
                {/* Only show the button, not the QR code or title */}
                <ReferralLink minimal={true} />
              </div>
            </div>
            {/* Form fields - new layout */}
            <form className="px-8 pb-8 text-left">
              {/* General Information */}
              <div className="mb-8">
                <h2 className="mb-6 text-lg font-semibold">
                  General Information
                </h2>
                <div className="grid grid-cols-1 gap-x-20 gap-y-8 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Full Name
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-full border-none bg-gray-50 px-6 py-3 text-base text-gray-900 focus:ring-2 focus:ring-blue-200"
                      value={editMode ? form.fullName : profile.fullName}
                      disabled={!editMode || saving}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, fullName: e.target.value }))
                      }
                      placeholder="Full Name"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="w-full rounded-full border-none bg-gray-50 px-6 py-3 text-base text-gray-900 focus:ring-2 focus:ring-blue-200"
                      value={editMode ? form.email : profile.email}
                      disabled={!editMode || saving}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, email: e.target.value }))
                      }
                      placeholder="Email Address"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Phone
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-full border-none bg-gray-50 px-6 py-3 text-base text-gray-900 focus:ring-2 focus:ring-blue-200"
                      value={editMode ? form.phone : profile.phone}
                      disabled={!editMode || saving}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, phone: e.target.value }))
                      }
                      placeholder="Phone"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Address
                    </label>
                    <input
                      type="text"
                      className="w-full rounded-full border-none bg-gray-50 px-6 py-3 text-base text-gray-900 focus:ring-2 focus:ring-blue-200"
                      value={form.address || ""}
                      disabled={!editMode || saving}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, address: e.target.value }))
                      }
                      placeholder="Address"
                    />
                  </div>
                </div>
              </div>

              {/* Account Security */}
              <div className="mb-8">
                <h2 className="mb-6 text-lg font-semibold">Account Security</h2>
                <div className="grid grid-cols-1 gap-x-20 gap-y-8 md:grid-cols-2">
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      Current Password
                    </label>
                    <input
                      type="password"
                      className="w-full rounded-full border-none bg-gray-50 px-6 py-3 text-base text-gray-900 focus:ring-2 focus:ring-blue-200"
                      value={form.currentPassword || ""}
                      disabled={!editMode || saving}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          currentPassword: e.target.value,
                        }))
                      }
                      placeholder="********************"
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">
                      New Password
                    </label>
                    <input
                      type="password"
                      className="w-full rounded-full border-none bg-gray-50 px-6 py-3 text-base text-gray-900 focus:ring-2 focus:ring-blue-200"
                      value={form.newPassword || ""}
                      disabled={!editMode || saving}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, newPassword: e.target.value }))
                      }
                      placeholder="New Password"
                    />
                  </div>
                </div>
              </div>
            </form>
            {saveError && (
              <div className="px-8 pb-2 font-medium text-red-500">
                {saveError}
              </div>
            )}
            {passwordChangeStatus && (
              <div
                className={`px-8 pb-2 font-medium ${passwordChangeStatus.includes("success") ? "text-green-600" : "text-red-500"}`}
              >
                {passwordChangeStatus}
              </div>
            )}
            {editMode && (
              <div className="fixed bottom-8 right-8 z-50">
                <button
                  className="rounded-full bg-blue-600 px-10 py-3 text-lg font-semibold text-white shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  onClick={handleSave}
                  disabled={saving}
                  type="button"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            )}
            {/* Add Payment Method Button at the very bottom of the card */}
            {/* <div className="px-8 pb-8">
              <button className="rounded-lg border border-blue-400 bg-blue-50 text-blue-600 px-6 py-3 font-medium hover:bg-blue-100">
                + Add Payment Method
              </button>
            </div> */}
          </div>
        </div>
      </div>
    </>
  );
};

export default BuyerProfile;
